/**
 * Componentes UI base — design system mínimo para o painel admin.
 * Usa Tailwind CSS; sem dependências externas de UI.
 */
import {
  type ReactNode,
  type ButtonHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react'
import { cn } from '../../lib/utils.js'
import { useI18n } from '../../lib/i18n.js'

// ─── Button ───────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, children, className, disabled, ...props }, ref) => {
    const base =
      // PRATZU: raio 6, contorno 2px berinjela, sombra sólida que "afunda" ao clicar (guia de marca §08)
      'tm-admin-btn inline-flex items-center justify-center gap-2 font-extrabold rounded-md transition-[transform,box-shadow] focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
    const variants: Record<ButtonVariant, string> = {
      // açafrão com texto berinjela (nunca branco sobre açafrão)
      primary:
        'bg-pratzu-saffron text-pratzu-aubergine border-2 border-pratzu-aubergine shadow-tm-btn hover:bg-pratzu-saffron-press focus:ring-pratzu-tomato-ink',
      secondary:
        'bg-white text-pratzu-aubergine border-2 border-pratzu-aubergine shadow-tm-btn hover:bg-pratzu-cream focus:ring-pratzu-tomato-ink',
      ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-400',
      danger:
        'bg-pratzu-tomato text-white border-2 border-pratzu-aubergine shadow-tm-btn hover:bg-pratzu-tomato-ink focus:ring-pratzu-tomato-ink',
    }
    const sizes: Record<ButtonSize, string> = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-5 py-2.5 text-base',
    }
    return (
      <button
        ref={ref}
        disabled={disabled ?? loading}
        className={cn(base, variants[variant], sizes[size], className)}
        {...props}
      >
        {loading && (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'

// ─── Input ────────────────────────────────────────────────────────────────────
// Extraído para ui/Input.tsx (limite ~500 linhas/arquivo); reexportado aqui
// para não mudar nenhum import (`import { Input } from '../ui/index.js'`).

export { Input } from './Input.js'

// ─── Textarea ─────────────────────────────────────────────────────────────────

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, className, id, 'aria-describedby': ariaDescribedBy, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    const errorId = error && inputId ? `${inputId}-error` : undefined
    const describedBy = [ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          rows={3}
          className={cn(
            'w-full rounded-lg border bg-white px-3 py-2 text-sm shadow-sm placeholder-gray-400 resize-y',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
            error ? 'border-red-400' : 'border-gray-300',
            className,
          )}
          {...props}
          aria-invalid={error ? true : props['aria-invalid']}
          aria-describedby={describedBy}
        />
        {error && (
          <p id={errorId} className="text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
    )
  },
)
Textarea.displayName = 'Textarea'

// ─── Select ───────────────────────────────────────────────────────────────────

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  options: { value: string; label: string }[]
  /** #428: lang por opção (WCAG 3.1.2) — código do idioma em cada `option` */
  optionLang?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, className, id, optionLang = false, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={inputId}
          className={cn(
            'w-full rounded-lg border bg-white px-3 py-2 text-sm shadow-sm',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
            error ? 'border-red-400' : 'border-gray-300',
            className,
          )}
          {...props}
        >
          {options.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              {...(optionLang ? { lang: opt.value, dir: opt.value === 'ar' ? 'rtl' : 'ltr' } : {})}
            >
              {opt.label}
            </option>
          ))}
        </select>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    )
  },
)
Select.displayName = 'Select'

// ─── Switch ───────────────────────────────────────────────────────────────────

interface SwitchProps {
  checked: boolean
  onChange: (v: boolean) => void
  label?: string
  description?: string
  disabled?: boolean
  /** data-testid do botão (convenção em docs/DATA-TESTID.md) */
  testId?: string
}

export function Switch({ checked, onChange, label, description, disabled, testId }: SwitchProps) {
  return (
    <label
      className={cn(
        'flex items-center gap-3 cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
          checked ? 'bg-indigo-600' : 'bg-gray-200',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </button>
      {(label ?? description) && (
        <span>
          {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </span>
      )}
    </label>
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────

interface CardProps {
  children: ReactNode
  className?: string
  title?: string
  description?: string
  action?: ReactNode
}

export function Card({ children, className, title, description, action }: CardProps) {
  return (
    <div
      className={cn(
        'bg-white rounded-xl border-2 border-pratzu-aubergine shadow-tm-card',
        className,
      )}
    >
      {(title ?? action) && (
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
          <div>
            {title && <h3 className="text-base font-extrabold text-pratzu-aubergine">{title}</h3>}
            {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
          </div>
          {action && <div className="ml-4 flex-shrink-0">{action}</div>}
        </div>
      )}
      <div className="px-6 py-4">{children}</div>
    </div>
  )
}

// ─── Badge ────────────────────────────────────────────────────────────────────

type BadgeColor = 'green' | 'red' | 'yellow' | 'blue' | 'gray' | 'indigo'

interface BadgeProps {
  children: ReactNode
  color?: BadgeColor
  className?: string
}

export function Badge({ children, color = 'gray', className }: BadgeProps) {
  const colors: Record<BadgeColor, string> = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    blue: 'bg-blue-100 text-blue-700',
    gray: 'bg-gray-100 text-gray-600',
    indigo: 'bg-indigo-100 text-indigo-700',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        colors[color],
        className,
      )}
    >
      {children}
    </span>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)
  // #489 M10 (a11y): nome acessível do ✕ — ícone SVG sozinho não tem nome;
  // mesmo rótulo do botão «Fechar» dos rodapés (precedente ExpiredSessionNotice)
  const { t } = useI18n()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const sizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center p-4 top-[var(--impersonation-banner-h,0px)]"
      role="dialog"
      aria-modal
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        ref={ref}
        className={cn(
          // max-h-full: cabe no contentor (já limitado pela faixa); scroll só no corpo
          'relative bg-white rounded-2xl shadow-xl w-full flex flex-col max-h-full min-h-0',
          sizes[size],
        )}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            aria-label={t('admin.common.close')}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                d="M6 6l8 8M6 14L14 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Toast ────────────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'info'

interface ToastMessage {
  id: number
  message: string
  type: ToastType
}

let toastId = 0
const listeners: ((toasts: ToastMessage[]) => void)[] = []
let toasts: ToastMessage[] = []

function notify(toastList: ToastMessage[]) {
  toasts = toastList
  listeners.forEach((fn) => fn(toasts))
}

export const toast = {
  success: (message: string) => {
    const id = ++toastId
    notify([...toasts, { id, message, type: 'success' }])
    setTimeout(() => notify(toasts.filter((t) => t.id !== id)), 3500)
  },
  error: (message: string) => {
    const id = ++toastId
    notify([...toasts, { id, message, type: 'error' }])
    setTimeout(() => notify(toasts.filter((t) => t.id !== id)), 5000)
  },
  info: (message: string) => {
    const id = ++toastId
    notify([...toasts, { id, message, type: 'info' }])
    setTimeout(() => notify(toasts.filter((t) => t.id !== id)), 3500)
  },
}

export function Toaster() {
  const [list, setList] = useState<ToastMessage[]>([])

  useEffect(() => {
    const fn = (t: ToastMessage[]) => setList([...t])
    listeners.push(fn)
    return () => {
      const idx = listeners.indexOf(fn)
      if (idx !== -1) listeners.splice(idx, 1)
    }
  }, [])

  const icons: Record<ToastType, string> = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
  }
  const colors: Record<ToastType, string> = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-indigo-600',
  }

  return (
    // #474 (UX r2): anúncio para tecnologias de assistência + posição lógica (RTL)
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 end-4 z-[100] flex flex-col gap-2"
    >
      {list.map((t) => (
        <div
          key={t.id}
          // UX r3 ponto 9: erro anuncia ASSERTIVO (role=alert); o resto fica
          // polite no contentor. Ícone é decorativo.
          role={t.type === 'error' ? 'alert' : 'status'}
          className="flex items-center gap-3 bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-3 min-w-[280px] animate-in"
        >
          <span
            aria-hidden="true"
            className={cn(
              'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold',
              colors[t.type],
            )}
          >
            {icons[t.type]}
          </span>
          <p className="text-sm text-gray-800">{t.message}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

interface EmptyProps {
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
}

export function Empty({ title, description, action, icon }: EmptyProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {icon && <div className="mb-3 text-gray-300">{icon}</div>}
      <p className="text-sm font-medium text-gray-700">{title}</p>
      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span className="inline-flex items-center" role={label ? 'status' : undefined}>
      {label ? <span className="sr-only">{label}</span> : null}
      <svg
        className={cn('animate-spin h-5 w-5 text-indigo-600', className)}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden={label ? true : undefined}
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
    </span>
  )
}
